<template>
    <v-container fluid fill-height>
        <v-layout>
            <v-flex>
                <v-card>
                    <v-container>
                        <v-layout wrap class="action-buttons-container">
                            <v-flex xs12 class="text-xs-center">
                                <h3>Filter Personnel</h3>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-autocomplete
                                    box
                                    :items="employeeStatuses"
                                    item-text="Name"
                                    item-value="Value"
                                    v-model="search.status"
                                    label="Status"
                                    hint="Status"
                                    placeholder="Status"
                                    clearable
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-text-field
                                    box
                                    v-model="search.code"
                                    label="Employee Code"
                                    clearable>
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-text-field
                                    box
                                    v-model="search.FirstName"
                                    label="First Name"
                                    clearable>
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-text-field
                                    box
                                    v-model="search.LastName"
                                    label="Last Name"
                                    clearable>
                                </v-text-field>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getPlantsWithTeamsSortedArray"
                                    item-text="PlantName"
                                    item-value="PlantID"
                                    v-model="search.plantID"
                                    label="Current Plant"
                                    hint="Current Plant"
                                    placeholder="Current Plant"
                                    clearable
                                    :disabled="loadingPlants"
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getTeamsForPlant(search.plantID)"
                                    item-text="Name"
                                    item-value="TeamID"
                                    v-model="search.teamID"
                                    label="Current Team"
                                    hint="Current Team"
                                    placeholder="Current Team"
                                    clearable
                                    :disabled="!search.plantID"
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getLevelsForPlant(search.plantID)"
                                    item-text="NameWithWorkType"
                                    item-value="RoleTypeID"
                                    v-model="search.levelID"
                                    label="Current Level"
                                    hint="Current Level"
                                    placeholder="Current Level"
                                    clearable
                                    :disabled="loadingLevels || !search.plantID"
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm6 offset-sm3>
                                <v-btn block color="accent" :loading="loading || savingUpdates" @click="filter()">Filter</v-btn>
                            </v-flex>
                        </v-layout>

                        <v-layout wrap class="bulk-updates-container">
                            <v-flex xs12 class="text-xs-center">
                                <h3>Bulk Update</h3>
                            </v-flex>
                            <v-flex xs12 class="text-xs-center">
                                <p>The selections will be applied to any personnel matching the filters above. If there are conflicts with any individual selections, below, a warning will be shown.</p>
                                <v-alert :value="showBulkUpdateWarning" color="info" outline icon="info">
                                    You have changed the search filters and not updated the results by pressing the Filter button. To see an accurate representation of the personnel that the bulk update selections will be applied to, please press the Filter button again.
                                </v-alert>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getPlantsWithTeamsSortedArray"
                                    item-text="PlantName"
                                    item-value="PlantID"
                                    v-model="bulk.plantID"
                                    label="New Plant"
                                    hint="New Plant"
                                    placeholder="New Plant"
                                    clearable
                                    :disabled="loadingPlants"
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getTeamsForPlant(bulk.plantID)"
                                    item-text="NameWithWorkType"
                                    item-value="TeamID"
                                    v-model="bulk.teamID"
                                    label="New Team"
                                    hint="New Team"
                                    placeholder="New Team"
                                    clearable
                                    :disabled="!bulk.plantID"
                                ></v-autocomplete>
                            </v-flex>
                            <v-flex xs12 sm4>
                                <v-autocomplete
                                    box
                                    :items="getLevelsForPlant(bulk.plantID)"
                                    item-text="NameWithWorkType"
                                    item-value="RoleTypeID"
                                    v-model="bulk.levelID"
                                    label="New Level"
                                    hint="New Level"
                                    placeholder="New Level"
                                    clearable
                                    :disabled="loadingLevels || !bulk.plantID"
                                ></v-autocomplete>
                            </v-flex>
                        </v-layout>

                        <v-layout wrap class="action-buttons-container">
                            <v-flex xs12 sm3 offset-sm3>
                                <v-btn block color="error" :disabled="loading || savingUpdates" @click="cancelChanges()">Cancel</v-btn>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-btn block color="primary" :disabled="loading || savingUpdates" @click="confirmChanges()">Save</v-btn>
                            </v-flex>
                            <v-flex xs12>
                                <v-alert :value="showPersonnelInOpenAttendanceError" color="error">One or more personnel is in an active attendance and was not updated.</v-alert>
                            </v-flex>
                        </v-layout>

                        <v-layout wrap>
                            <v-flex xs12 class="text-xs-center">
                                <h3>Individual Update</h3>
                            </v-flex>
                            <v-flex xs12>
                                <v-data-table
                                    :headers="headers" 
                                    :items="personnel"
                                    :loading="loading"
                                    no-data-text=""
                                    :pagination.sync="pagination"
                                    :rows-per-page-items="rowsPerPageItems"
                                    :total-items="personnelTotal"
                                >
                                    <template v-slot:progress>
                                        <v-progress-linear color="primary" indeterminate></v-progress-linear>
                                    </template>
                                    <template v-slot:items="props">
                                        <PersonnelMovementRow :item="props.item"></PersonnelMovementRow>
                                    </template>
                                    <template v-slot:no-data v-if="!loading">
                                        <v-alert :value="true" color="error" icon="warning" outline>No available personnel found</v-alert>
                                    </template>
                                </v-data-table> 
                            </v-flex>
                        </v-layout>

                        <v-layout wrap class="action-buttons-container">
                            <v-flex xs12 sm3 offset-sm3>
                                <v-btn block color="error" :disabled="loading || savingUpdates" @click="cancelChanges()">Cancel</v-btn>
                            </v-flex>
                            <v-flex xs12 sm3>
                                <v-btn block color="primary" :disabled="loading || savingUpdates" @click="confirmChanges()">Save</v-btn>
                            </v-flex>
                        </v-layout>
                    </v-container>

                    <v-dialog v-model="showConfirmSavePopup" transition="dialog-bottom-transition" max-width="800px" @keydown.esc="cancelConfirmChanges">
                        <v-card>
                            <v-toolbar dark color="primary">
                                <v-btn icon dark :disabled="savingUpdates" @click="cancelConfirmChanges">
                                    <v-icon large>close</v-icon>
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-toolbar-title>Confirm Personnel Movement Changes</v-toolbar-title>
                                <v-spacer></v-spacer>
                                <v-toolbar-items>
                                    
                                </v-toolbar-items>
                            </v-toolbar>
                            <v-layout class="dialog-content" wrap>
                                <v-flex xs12 v-if="!changesValidationMessage">
                                    Are you sure you want to make the selected changes?
                                </v-flex>
                                <v-flex v-else>
                                    {{changesValidationMessage}}
                                </v-flex>
                            </v-layout>
                            <v-layout class="dialog-action-buttons" wrap>
                                <v-flex xs12 sm6>
                                    <v-btn block color="error" :disabled="savingUpdates" @click="cancelConfirmChanges">Cancel</v-btn>
                                </v-flex>
                                <v-flex xs12 sm6>
                                    <v-btn block color="success" :disabled="!!changesValidationMessage" :loading="savingUpdates" @click="saveChanges">Save</v-btn>
                                </v-flex>
                            </v-layout>
                            <v-layout class="dialog-content" wrap>
                            <v-flex xs12>
                                <v-alert :value="saveError" color="error">Error saving data: {{saveError}}</v-alert>
                            </v-flex>
                        </v-layout>
                        </v-card>
                    </v-dialog>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import { mapState } from "vuex";
import { mapGetters } from "vuex";

import PersonnelMovementRow from "@/components/movement/PersonnelMovementRow";
 
export default {
    data() {
        return {
            showConfirmSavePopup: false,
            changesValidationMessage: "",
            search: {
                status: 0,
                code: null,
                firstName: null,
                lastName: null,
                plantID: null,
                teamID: null,
                levelID: null
            },
            searchChanged: false,
            bulk: {
                plantID: null,
                teamID: null,
                levelID: null
            },
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false,
                sortBy: "Name"
            },
            rowsPerPageItems: [10, 25, 50, 100, {"text":"$vuetify.dataIterator.rowsPerPageAll","value":-1}],
            headers: [
                {
                    text: "Employee Code",
                    value: "EmployeeCode",
                },
                {
                    text: "Name",
                    value: "Name",
                },
                {
                    text: "Plant",
                    value: "Plant",
                },
                {
                    text: "Team",
                    value: "Team",
                },
                {
                    text: "Level",
                    value: "Level"
                },
                {
                    text: "New Plant",
                    value: "NewPlant",
                    sortable: false
                },
                {
                    text: "New Team",
                    value: "NewTeam",
                    sortable: false
                },
                {
                    text: "New Level",
                    value: "NewLevel",
                    sortable: false
                }
            ]
        }
    },
    components: {
        PersonnelMovementRow
    },
    created() {
        this.loadInitialData();
    },
    computed: {
        ...mapState("PersonnelMovementsStore", {
            plantsWithTeams: state => state.plantsWithTeams,
            levels: state => state.levels,
            personnel: state => state.personnel,
            employeeStatuses: state => state.employeeStatuses,
            loadingPlants: state => state.loadingPlants,
            loadingLevels: state => state.loadingLevels,
            personnelTotal: state => state.personnelTotal,
            savingUpdates: state => state.savingUpdates,
            showPersonnelInOpenAttendanceError: state => state.showPersonnelInOpenAttendanceError,
            saveError: state => state.saveError
        }),
        ...mapGetters({
            getTeamsForPlant: "PersonnelMovementsStore/getTeamsForPlant",
            getLevelsForPlant: "PersonnelMovementsStore/getLevelsForPlant",
            loading: "PersonnelMovementsStore/loading",
            getPlantsWithTeamsSortedArray: "PersonnelMovementsStore/getPlantsWithTeamsSortedArray"
        }),
        bulkPlantID() {
            return this.bulk.plantID;
        },
        searchPlantID() {
            return this.search.plantID;
        },
        showBulkUpdateWarning() {
            return this.searchChanged && (this.bulk.plantID || this.bulk.teamID || this.bulk.levelID);
        },
        hasBulkSelections() {
            return (this.bulk.plantID && this.bulk.plantID > 0) || (this.bulk.teamID && this.bulk.teamID > 0) || (this.bulk.levelID && this.bulk.levelID > 0);
        }
    },
    methods: {
        async checkChanges() {
            // first clear any previous message            
            this.changesValidationMessage = "";

            let hasIndividualData = await this.$store.dispatch("PersonnelMovementsStore/hasIndividualUpdateData");

            // if there is no data, show a message
            if (!this.hasBulkSelections && !hasIndividualData) {
                this.changesValidationMessage = "Please make at least one bulk or individual update selection to continue.";
            }
            
            // now we need to check for conflicts between the bulk selectins and individual selections
            let conflict = await this.$store.dispatch("PersonnelMovementsStore/isConflictBetweenBulkAndIndividual", this.bulk);

            if (conflict) {
                this.changesValidationMessage = "You cannot select both a bulk and an individual update for plants, teams, or levels. Please remove some selections and try again.";
            }

            let workTypesMatch = await this.$store.dispatch("PersonnelMovementsStore/workTypesMatch", this.bulk);

            if(!workTypesMatch) {
                this.changesValidationMessage = "Work Types (Picking / Crop Work) between Team and Level need to match";
            }
        },
        cancelChanges() {
            // just clear the bulk selections and refilter the results (wiping the individual updates is part of the filter action)

            this.bulk.plantID = null;
            this.bulk.teamID = null;
            this.bulk.levelID = null;

            // go back to the first page
            this.pagination.page = 1;

            this.filter();
        },
        async confirmChanges() {            
            await this.$store.dispatch("PersonnelMovementsStore/clearSaveError");            
            await this.checkChanges();

            this.showConfirmSavePopup = true;
        },
        cancelConfirmChanges() {
            this.showConfirmSavePopup = false;
        },
        async saveChanges() {
            let payload = {
                bulk: this.bulk,
                search: this.search
            };

            await this.$store.dispatch("PersonnelMovementsStore/saveUpdates", payload);

            if (!this.saveError) {
                // if there was no error, then refresh the grid data
                // also clear the bulk selections and stop showing the confim popup
                this.cancelConfirmChanges();
                this.cancelChanges();
            }
        },
        async loadInitialData() {
            this.$store.dispatch("PersonnelMovementsStore/getPlants");
            this.$store.dispatch("PersonnelMovementsStore/getLevels");
            this.filter();
        },
        async filter() {
            let params = {...this.search, ...this.pagination};

            await this.$store.dispatch("PersonnelMovementsStore/getPersonnel", params);

            // this flag is just tracking whether we've changed the filters since the last filter operation completed - so reset it here
            this.searchChanged = false;
        }
    },
    watch: {
        bulkPlantID() {
            // clear team selection when plant selection changes
            this.bulk.teamID = null;
        },
        searchPlantID() {
            // clear team selection when plant selection changes
            this.search.teamID = null;
        },
        search: {
            handler() {
                this.searchChanged = true;
            },
            deep: true
        },
        pagination: {
            handler() {
                this.filter();
            },
            deep: true
        }
    }
}
</script>

<style>
    .action-buttons-container .flex,
    .bulk-updates-container .flex {
        padding-left: 5px;
        padding-right: 5px;
    }

    .dialog-content {
        padding: 6px;
    }
</style>
